import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, Loader2, LogOut, Mail, RefreshCw, Search, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/constants/hotelData";
import { Logo } from "@/components/Logo";

const fmtDate = (s) =>
  new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });

const PAY_BADGE = {
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  pay_at_hotel: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  failed: "bg-red-500/15 text-red-300 border-red-400/30",
};
const STATUS_BADGE = {
  confirmed: "bg-gold/15 text-goldlight border-gold/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-400/30",
  checked_in: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  completed: "bg-stone-500/15 text-stone-300 border-stone-400/30",
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "pending", label: "Pending" },
  { id: "pay_at_hotel", label: "Pay at Hotel" },
  { id: "cancelled", label: "Cancelled" },
];

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/bookings");
    setBookings(data);
  };

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("hr_token");
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await load();
      } catch {
        localStorage.removeItem("hr_token");
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("hr_token", data.access_token);
      setUser(data.user);
      await load();
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    localStorage.removeItem("hr_token");
    setUser(null);
    setBookings([]);
  };

  const updateBooking = async (ref, patch) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${ref}`, patch);
      setBookings((prev) => prev.map((b) => (b.ref === ref ? data : b)));
      toast.success(`Booking ${ref} updated`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markPaid = (ref) => updateBooking(ref, { payment_status: "paid" });
  const cancel = (ref) => {
    if (window.confirm(`Cancel booking ${ref}?`)) updateBooking(ref, { booking_status: "cancelled" });
  };
  const restore = (ref) => updateBooking(ref, { booking_status: "confirmed" });
  const sendReceipt = async (ref) => {
    try {
      const { data } = await api.post(`/admin/bookings/${ref}/send-receipt`);
      setBookings((prev) => prev.map((b) => (b.ref === ref ? data : b)));
      toast.success(`Receipt emailed to ${data.email}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const verifyPayment = async (ref) => {
    try {
      const { data } = await api.post(`/admin/bookings/${ref}/verify-payment`);
      setBookings((prev) => prev.map((b) => (b.ref === ref ? data : b)));
      toast[data.payment_status === "paid" ? "success" : "info"](`CCAvenue status: ${data.cca_order_status || "no payment found"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const stats = useMemo(() => {
    const active = bookings.filter((b) => b.booking_status !== "cancelled");
    return {
      total: bookings.length,
      revenue: bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + b.amount, 0),
      rooms: active.reduce((s, b) => s + b.rooms, 0),
      due: active.filter((b) => b.payment_status === "pending" || b.payment_status === "pay_at_hotel").length,
    };
  }, [bookings]);

  const filtered = bookings.filter((b) => {
    const byFilter =
      filter === "all"
        ? true
        : filter === "cancelled"
          ? b.booking_status === "cancelled"
          : b.payment_status === filter && b.booking_status !== "cancelled";
    const needle = q.trim().toLowerCase();
    const bySearch =
      !needle ||
      b.ref.toLowerCase().includes(needle) ||
      b.guest_name.toLowerCase().includes(needle) ||
      b.phone.includes(needle);
    return byFilter && bySearch;
  });

  const inputCls =
    "w-full rounded-lg border border-cream/15 bg-ink/50 px-3.5 py-2.5 text-sm text-cream outline-none transition-colors placeholder:text-cream/35 focus:border-gold/60";

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-gold">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-5 text-cream">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="shimmer-border w-full max-w-md rounded-2xl p-8 sm:p-10">
          <div className="flex justify-center"><Logo compact /></div>
          <h1 className="mt-6 text-center font-serif text-3xl">
            Admin <span className="gold-gradient-text italic">Portal</span>
          </h1>
          <p className="mt-2 flex items-center justify-center gap-2 text-center text-xs font-light text-stone-400">
            <ShieldCheck className="h-4 w-4 text-gold" /> Hotel Raj — owner access only
          </p>
          <form onSubmit={login} className="mt-8 space-y-5">
            <div>
              <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Email</label>
              <input data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@hotelraj.com" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Password</label>
              <input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300">
                {error}
              </p>
            )}
            <button data-testid="admin-login-submit" disabled={loggingIn} className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-all enabled:hover:bg-goldlight disabled:opacity-60">
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-5 py-10 text-cream sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden h-8 w-px bg-gold/20 sm:block" />
            <h1 className="hidden font-serif text-2xl sm:block">Admin Dashboard</h1>
          </div>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-cream/25 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cream/80 transition-colors hover:border-gold hover:text-gold">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: "Total Bookings", value: stats.total, testid: "stat-total-bookings" },
            { label: "Revenue (Paid)", value: inr(stats.revenue), testid: "stat-revenue" },
            { label: "Rooms Booked", value: `${stats.rooms} / 20`, testid: "stat-rooms-booked" },
            { label: "Payments Due", value: stats.due, testid: "stat-payments-due" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-gold/15 bg-[#0f241b] p-5" data-testid={s.testid}>
              <div className="text-[10px] uppercase tracking-[0.24em] text-cream/60">{s.label}</div>
              <div className="mt-2 font-serif text-2xl text-gold sm:text-3xl">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`admin-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full border px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.16em] transition-all ${
                  filter === f.id ? "border-gold bg-gold text-ink" : "border-cream/20 text-cream/70 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-cream/40" />
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, booking ID…"
              className={`${inputCls} pl-10`}
            />
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-gold/15" data-testid="admin-bookings-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-[#0f241b] text-[10px] uppercase tracking-[0.2em] text-cream/60">
              <tr>
                {["Booking", "Guest", "Stay", "Rooms", "Amount", "Payment", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm font-light text-stone-500">
                    No bookings found — new bookings will appear here instantly.
                  </td>
                </tr>
              )}
              {filtered.map((b) => (
                <tr key={b.ref} data-testid={`admin-row-${b.ref}`} className="border-t border-gold/10 transition-colors hover:bg-gold/5">
                  <td className="px-4 py-4 font-mono text-xs text-gold">{b.ref}</td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-cream">{b.guest_name}</div>
                    <div className="text-xs text-stone-400">{b.phone}</div>
                  </td>
                  <td className="px-4 py-4 text-xs text-stone-300">
                    {fmtDate(b.check_in)} → {fmtDate(b.check_out)}
                    <span className="block text-stone-500">{b.nights} night{b.nights > 1 ? "s" : ""}</span>
                  </td>
                  <td className="px-4 py-4">{b.rooms}</td>
                  <td className="px-4 py-4 font-medium text-goldlight">{inr(b.amount)}</td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-wider ${PAY_BADGE[b.payment_status] || ""}`}>
                      {b.payment_status.replace(/_/g, " ")}
                    </span>
                    {b.cca_tracking_id && (
                      <span className="mt-1 block font-mono text-[10px] text-stone-500" data-testid={`tracking-${b.ref}`}>CCA #{b.cca_tracking_id}</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-wider ${STATUS_BADGE[b.booking_status] || ""}`}>
                      {b.booking_status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      {b.payment_status === "paid" && b.email && (
                        <button data-testid={`send-receipt-${b.ref}`} onClick={() => sendReceipt(b.ref)} title={b.receipt_sent_at ? "Receipt already sent — resend" : "Email receipt to guest"} className="flex items-center gap-1 rounded-full border border-gold/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-goldlight transition-colors hover:bg-gold/10">
                          <Mail className="h-3.5 w-3.5" /> {b.receipt_sent_at ? "Resend" : "Receipt"}
                        </button>
                      )}
                      {b.payment_status !== "paid" && b.booking_status !== "cancelled" && b.cca_order_id && (
                        <button data-testid={`verify-payment-${b.ref}`} onClick={() => verifyPayment(b.ref)} title="Re-check with CCAvenue" className="flex items-center gap-1 rounded-full border border-sky-400/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-sky-300 transition-colors hover:bg-sky-400/10">
                          <RefreshCw className="h-3.5 w-3.5" /> Verify
                        </button>
                      )}
                      {b.payment_status !== "paid" && b.booking_status !== "cancelled" && (
                        <button data-testid={`mark-paid-${b.ref}`} onClick={() => markPaid(b.ref)} className="flex items-center gap-1 rounded-full border border-emerald-400/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-emerald-300 transition-colors hover:bg-emerald-400/10">
                          <BadgeCheck className="h-3.5 w-3.5" /> Paid
                        </button>
                      )}
                      {b.booking_status === "cancelled" ? (
                        <button data-testid={`restore-${b.ref}`} onClick={() => restore(b.ref)} className="rounded-full border border-cream/25 px-3 py-1.5 text-[10px] uppercase tracking-wider text-cream/70 hover:border-gold hover:text-gold">
                          Restore
                        </button>
                      ) : (
                        <button data-testid={`cancel-${b.ref}`} onClick={() => cancel(b.ref)} className="rounded-full border border-red-400/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-red-300 transition-colors hover:bg-red-400/10">
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
